import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import type { DimensionView } from '../interpretiveReportView';
import { SectionHead } from './SectionHead';
import { toneClass } from './Tone';
import styles from '../InterpretiveReportPage.module.css';

/** The scale steps 1 to 5, as positions along the track from 0 to 100 */
const STEPS = [1, 2, 3, 4, 5].map((step, index) => ({ step, position: index * 25 }));

const SpreadRow = ({ dimension }: { dimension: DimensionView }) => {
  const { t } = useTranslation();
  const { mean, bandStart, bandWidth } = dimension.spread;
  return (
    <div className={styles.spreadRow}>
      <span className={styles.spreadName}>
        <span className={styles.spreadTitle}>{dimension.name}</span>
        <span className={styles.meta}>
          {t('interpretiveReport.document.spread.rowMeta', {
            score: dimension.score,
            sd: dimension.standardDeviation,
          })}
        </span>
      </span>
      <div aria-hidden="true" className={styles.track}>
        <div className={styles.trackLine} />
        {STEPS.map(({ step, position }) => (
          <span key={step} className={styles.tick} style={{ left: `${position}%` }} />
        ))}
        <span
          className={cn(styles.band, toneClass(dimension.status, 'Band'))}
          style={{ left: `${bandStart}%`, width: `${bandWidth}%` }}
        />
        <span className={cn(styles.mean, toneClass(dimension.status, 'Mean'))} style={{ left: `${mean}%` }} />
      </div>
    </div>
  );
};

export const SpreadChart = ({ number, dimensions }: { number: string; dimensions: DimensionView[] }) => {
  const { t } = useTranslation();
  return (
    <section>
      <SectionHead
        number={number}
        eyebrow={t('interpretiveReport.document.spread.eyebrow')}
        title={t('interpretiveReport.document.spread.title')}
        intro={t('interpretiveReport.document.spread.intro')}
      />
      <div className={cn(styles.spread, styles.keep)}>
        {dimensions.map((dimension) => (
          <SpreadRow key={dimension.name} dimension={dimension} />
        ))}
        <div className={styles.axis}>
          <span>{t('interpretiveReport.document.spread.scale')}</span>
          <div className={styles.axisLabels}>
            {STEPS.map(({ step, position }) => (
              <span key={step} className={styles.axisLabel} style={{ left: `${position}%` }}>
                {step}
              </span>
            ))}
          </div>
        </div>
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span aria-hidden="true" className={styles.legendDot} />
            {t('interpretiveReport.document.spread.mean')}
          </span>
          <span className={styles.legendItem}>
            <span aria-hidden="true" className={styles.legendBand} />
            {t('interpretiveReport.document.spread.band')}
          </span>
        </div>
      </div>
    </section>
  );
};
